"use client"

import { useState, useEffect } from "react"
import {
    UserGoalStep,
    TarotExperienceStep,
    DeckPreferenceStep,
    BirthDataStep,
    ReadingFrequencyStep,
    AccountStep,
    AIToneStep,
    CreditIntroStep,
    PrivacyStep,
    CompletionStep,
} from "./onboarding-steps"
import OnboardingStepper from "./onboarding-stepper"
import { ArrowLeft } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../../context/AuthContext"
import { useTranslation } from 'react-i18next';
import { track } from "../../third-party/MixPanel";

export default function OnboardingPage() {
    const { onboardUser, refreshUserData } = useAuth()
    const { t } = useTranslation();
    const [currentStep, setCurrentStep] = useState(0)
    const [formData, setFormData] = useState({
        user_goal: "",
        tarot_experience: "",
        deck_preference: "",
        birth_data: {
            birthdate: "",
            birth_time: "",
            birth_place: "",
        },
        reading_frequency: "",
        account: "",
        ai_tone_preference: "",
        credit_intro_ack: false,
        privacy_consent: false,
    })
    const navigate = useNavigate()

    // Track onboarding started when component mounts
    useEffect(() => {
        track('onboardingStarted', {
            timestamp: new Date().toISOString(),
            source: 'onboardingPage'
        });
    }, []);

    // Define the onboarding steps based on the JSON
    const steps = [
        { id: "user_goal", label: t('onboarding.steps.userGoal'), required: true },
        { id: "tarot_experience", label: t('onboarding.steps.experience'), required: true },
        /* { id: "deck_preference", label: t('onboarding.steps.deck'), required: false },*/
        { id: "birth_data", label: t('onboarding.steps.birthData'), required: false },
        { id: "reading_frequency", label: t('onboarding.steps.frequency'), required: true },
        { id: "ai_tone_preference", label: t('onboarding.steps.aiTone'), required: false },
        { id: "credit_intro_ack", label: t('onboarding.steps.credits'), required: true },
        { id: "privacy_consent", label: t('onboarding.steps.privacy'), required: true },
        { id: "completion", label: t('onboarding.steps.complete'), required: true },
    ]

    const updateFormData = (stepId: string, value: any) => {
        setFormData((prev) => ({
            ...prev,
            [stepId]: value,
        }))
    }

    const handleNext = () => {
        const currentStepId = steps[currentStep].id
        let canProceed = false

        switch (currentStepId) {
            case "user_goal":
                canProceed = !!formData.user_goal
                break
            case "tarot_experience":
                canProceed = !!formData.tarot_experience
                break
            case "deck_preference":
                canProceed = !steps[currentStep].required || !!formData.deck_preference
                break
            case "birth_data":
                canProceed = !steps[currentStep].required || (
                    !!formData.birth_data.birthdate &&
                    !!formData.birth_data.birth_time &&
                    !!formData.birth_data.birth_place
                )
                break
            case "reading_frequency":
                canProceed = !!formData.reading_frequency
                break
            case "ai_tone_preference":
                canProceed = !steps[currentStep].required || !!formData.ai_tone_preference
                break
            case "account":
                canProceed = !!formData.account
                break
            case "credit_intro_ack":
                canProceed = formData.credit_intro_ack
                break
            case "privacy_consent":
                canProceed = formData.privacy_consent
                break
            case "completion":
                canProceed = true
                break
            default:
                canProceed = false
        }

        if (canProceed && currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1)
        }
    }

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1)
        }
    }

    const handleSkip = () => {
        if (!steps[currentStep].required && currentStep < steps.length - 1) {
            setCurrentStep(currentStep + 1)
        }
    }

    const handleSubmit = async () => {
        try {
            const token = localStorage.getItem('token')
            if (!token) {
                throw new Error('No authentication token found')
            }

            // The form data now contains English values directly, so no conversion needed
            const englishFormData = formData;

            // Send onboarding data to backend
            const response = await fetch(`${import.meta.env.VITE_API_URL}/api/user/onboarding`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(englishFormData),
            })

            if (!response.ok) {
                throw new Error('Failed to save onboarding data')
            }

            // Track onboarding finished successfully
            track('onboarding_finished', {
                timestamp: new Date().toISOString(),
                userGoal: formData.user_goal,
                tarotExperience: formData.tarot_experience,
                deckPreference: formData.deck_preference,
                readingFrequency: formData.reading_frequency,
                aiTonePreference: formData.ai_tone_preference,
                hasBirthData: !!(formData.birth_data.birthdate && formData.birth_data.birth_time && formData.birth_data.birth_place),
                completionSource: 'onboarding_page'
            });

            // Refresh user data to get updated onboarding status
            await onboardUser({ onboarding: true, profile: englishFormData })
            await refreshUserData()
            navigate('/')
        } catch (error) {
            console.error('Error saving onboarding data:', error)
            // Track onboarding failure
            track('onboardingFailed', {
                timestamp: new Date().toISOString(),
                error: error instanceof Error ? error.message : 'Unknown error',
                step: 'submission'
            });
            // You might want to add error handling UI here
        }
    }

    const renderStep = () => {
        const stepId = steps[currentStep].id

        switch (stepId) {
            case "user_goal":
                return (
                    <UserGoalStep
                        value={formData.user_goal}
                        onChange={(value) => updateFormData("user_goal", value)}
                        onNext={handleNext}
                    />
                )
            case "tarot_experience":
                return (
                    <TarotExperienceStep
                        value={formData.tarot_experience}
                        onChange={(value) => updateFormData("tarot_experience", value)}
                        onNext={handleNext}
                    />
                )
            case "deck_preference":
                return (
                    <DeckPreferenceStep
                        value={formData.deck_preference}
                        onChange={(value) => updateFormData("deck_preference", value)}
                        onNext={handleNext}
                        onSkip={handleSkip}
                    />
                )
            case "birth_data":
                return (
                    <BirthDataStep
                        value={formData.birth_data}
                        onChange={(value) => updateFormData("birth_data", value)}
                        onNext={handleNext}
                        onSkip={handleSkip}
                    />
                )
            case "reading_frequency":
                return (
                    <ReadingFrequencyStep
                        value={formData.reading_frequency}
                        onChange={(value) => updateFormData("reading_frequency", value)}
                        onNext={handleNext}
                    />
                )
            case "ai_tone_preference":
                return (
                    <AIToneStep
                        value={formData.ai_tone_preference}
                        onChange={(value) => updateFormData("ai_tone_preference", value)}
                        onNext={handleNext}
                        onSkip={handleSkip}
                    />
                )
            case "account":
                return (
                    <AccountStep
                        value={formData.account}
                        onChange={(value) => updateFormData("account", value)}
                        onNext={handleNext}
                    />
                )
            case "credit_intro_ack":
                return (
                    <CreditIntroStep
                        value={formData.credit_intro_ack}
                        onChange={(value) => updateFormData("credit_intro_ack", value)}
                        onNext={handleNext}
                    />
                )
            case "privacy_consent":
                return (
                    <PrivacyStep
                        value={formData.privacy_consent}
                        onChange={(value) => updateFormData("privacy_consent", value)}
                        onNext={handleNext}
                    />
                )
            case "completion":
                return <CompletionStep onSubmit={handleSubmit} />
            default:
                return null
        }
    }

    return (
        <div className="min-h-screen bg-[#f8f5f1] flex flex-col">
            <header className="p-3 sm:p-4 flex items-center">
                {currentStep > 0 && (
                    <button
                        onClick={handleBack}
                        className="text-[#3d405b] hover:text-[#2a2c3d] transition-colors flex-shrink-0"
                        aria-label="Go back"
                    >
                        <ArrowLeft size={20} className="sm:w-6 sm:h-6" />
                    </button>
                )}
                <h1 className="text-lg sm:text-xl text-[#3d405b] font-serif mx-auto px-2">
                    {steps[currentStep].label}
                </h1>
            </header>

            <main className="flex-1 flex flex-col px-3 sm:px-4 pb-4 max-w-md mx-auto w-full">
                <OnboardingStepper
                    steps={steps}
                    currentStep={currentStep}
                    onStepClick={(index) => {
                        // Only allow going back to previous steps
                        if (index < currentStep) {
                            setCurrentStep(index)
                        }
                    }}
                    onNext={handleNext}
                    onBack={handleBack}
                />

                <div className="flex-1 flex flex-col ">{renderStep()}</div>
            </main>
        </div>
    )
}
